import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
    FileText,
    CheckCircle2,
    AlertCircle,
    Clock,
    Loader2,
    Building,
    Mail,
    Phone,
    MessageCircle,
    ArrowRight,
    Shield,
    Lock,
    User,
    ShoppingBag,
    Ticket,
    ExternalLink,
    QrCode
} from 'lucide-react'
import { clientService, invoiceService, settingsService } from '../services/firebase'
import { ncinqApiService } from '../services/ncinqApiService'

export default function PublicClientDashboard() {
    const { clientId } = useParams()
    const [client, setClient] = useState(null)
    const [invoices, setInvoices] = useState([])
    const [settings, setSettings] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // Global nCinq Activity
    const [globalProfile, setGlobalProfile] = useState(null)
    const [globalOrders, setGlobalOrders] = useState([])
    const [globalTickets, setGlobalTickets] = useState([])
    const [activeTab, setActiveTab] = useState('invoices') // 'invoices', 'global', 'tickets'

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true)

                // 1. Get Client Info
                const clientData = await clientService.getPublicClient(clientId)
                if (!clientData) {
                    setError('Client not found')
                    setLoading(false)
                    return
                }
                setClient(clientData)

                // 2. Get Business Settings (from the user who owns the client)
                if (clientData.userId) {
                    const settingsData = await settingsService.getPublicSettings(clientData.userId)
                    setSettings(settingsData)
                }

                // 3. Get Client Invoices
                const invoicesData = await invoiceService.getPublicClientInvoices(clientId)
                setInvoices(invoicesData)

                // 4. Get Global nCinq Data (Match by Email)
                if (clientData.email) {
                    const [profile, orders, tickets] = await Promise.all([
                        ncinqApiService.fetchUserProfile(clientData.email),
                        ncinqApiService.fetchUserOrders(clientData.email),
                        ncinqApiService.fetchUserTickets(clientData.email)
                    ])

                    if (profile) setGlobalProfile(profile)
                    if (orders) setGlobalOrders(orders)
                    if (tickets) setGlobalTickets(tickets)
                }

            } catch (err) {
                console.error('Error loading public client dashboard:', err)
                setError('Failed to load dashboard')
            } finally {
                setLoading(false)
            }
        }

        if (clientId) {
            loadData()
        }
    }, [clientId])

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={40} className="animate-spin text-primary-600" />
            </div>
        )
    }

    if (error || !client) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                        <AlertCircle size={32} className="text-red-500" />
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">Unavailable</h1>
                    <p className="text-gray-500">{error || "The client portal you're looking for doesn't exist."}</p>
                </div>
            </div>
        )
    }

    const getStatusConfig = (status) => {
        const configs = {
            draft: { bg: 'bg-gray-100', text: 'text-gray-700', icon: FileText, label: 'Draft' },
            sent: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'Pending' },
            viewed: { bg: 'bg-indigo-100', text: 'text-indigo-700', icon: Clock, label: 'Pending' },
            partial: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'Partial' },
            paid: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2, label: 'Paid' },
            overdue: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle, label: 'Overdue' },
        }
        return configs[status] || configs.draft
    }

    const stats = {
        totalBilled: invoices.reduce((sum, inv) => sum + inv.total, 0),
        paidAmount: invoices
            .reduce((sum, inv) => sum + (inv.partialAmount || (inv.status === 'paid' ? inv.total : 0)), 0),
        outstanding: invoices
            .filter(inv => ['sent', 'viewed', 'partial', 'overdue'].includes(inv.status))
            .reduce((sum, inv) => sum + (inv.total - (inv.partialAmount || 0)), 0),
    }

    // Formatting helper
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: settings?.currency || 'USD'
        }).format(amount)
    }

    const formatDate = (dateValue) => {
        if (!dateValue) return ''
        const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue)
        return date.toLocaleDateString()
    }

    const getWhatsAppLink = (phone) => {
        if (!phone) return ''
        const sanitized = phone.replace(/\D/g, '')
        return `https://wa.me/${sanitized}`
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white border-b border-gray-200">
                <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {settings?.logo ? (
                            <img src={settings.logo} alt={settings.businessName} className="h-10 w-auto object-contain" />
                        ) : (
                            <img src="/logo.png" alt="nCinq Invoice" className="h-10 w-auto" />
                        )}
                        <div>
                            <h1 className="font-bold text-gray-900">{settings?.businessName || 'nCinq Invoice'}</h1>
                            <p className="text-xs text-gray-500">Secure Client Portal</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Lock size={14} />
                        <span>Secure Access</span>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 py-8 space-y-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Hello, {client.businessName || client.name}</h1>
                        <p className="text-gray-600">Here is an overview of your invoices and global nCinq activity.</p>
                    </div>
                    {globalProfile && (
                        <div className="flex items-center gap-3 bg-white p-2 pr-4 rounded-full border border-gray-100 shadow-sm">
                            {globalProfile.photoURL ? (
                                <img src={globalProfile.photoURL} alt="" className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
                                    <User size={20} />
                                </div>
                            )}
                            <div>
                                <p className="text-xs text-gray-500 font-medium leading-none">nCinq Profile</p>
                                <p className="text-sm font-bold text-gray-900">{globalProfile.displayName || 'Client'}</p>
                            </div>
                            <span className="ml-2 w-2 h-2 rounded-full bg-green-500 animate-pulse" title="Synchronized"></span>
                        </div>
                    )}
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="card p-6 border-l-4 border-l-primary-500">
                        <p className="text-sm text-gray-500 font-medium mb-1">Outstanding Balance</p>
                        <p className="text-3xl font-bold text-gray-900">{formatCurrency(stats.outstanding)}</p>
                    </div>
                    <div className="card p-6">
                        <p className="text-sm text-gray-500 font-medium mb-1">Total Billed</p>
                        <p className="text-2xl font-bold text-gray-700">{formatCurrency(stats.totalBilled)}</p>
                    </div>
                    <div className="card p-6">
                        <p className="text-sm text-gray-500 font-medium mb-1">Total Paid</p>
                        <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.paidAmount)}</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-gray-100 shadow-sm w-fit">
                    <button
                        onClick={() => setActiveTab('invoices')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'invoices' ? 'bg-primary-50 text-primary-700 shadow-inner' : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Invoices
                    </button>
                    {(globalOrders.length > 0 || globalProfile) && (
                        <button
                            onClick={() => setActiveTab('global')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'global' ? 'bg-indigo-50 text-indigo-700 shadow-inner' : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Global Activity
                            <span className="ml-2 px-1.5 py-0.5 bg-indigo-100 text-indigo-800 text-[10px] rounded-full">{globalOrders.length}</span>
                        </button>
                    )}
                    {globalTickets.length > 0 && (
                        <button
                            onClick={() => setActiveTab('tickets')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'tickets' ? 'bg-accent-50 text-accent-700 shadow-inner' : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            My Tickets
                            <span className="ml-2 px-1.5 py-0.5 bg-accent-100 text-accent-800 text-[10px] rounded-full">{globalTickets.length}</span>
                        </button>
                    )}
                </div>

                {/* Tab Content */}
                <div className="space-y-6">
                    {/* Invoices Tab */}
                    {activeTab === 'invoices' && (
                        <div className="card animate-fade-in">
                            <div className="px-6 py-4 border-b border-gray-100">
                                <h2 className="font-semibold text-gray-900">Invoice History</h2>
                            </div>

                            {invoices.length === 0 ? (
                                <div className="p-12 text-center text-gray-500">
                                    <FileText size={48} className="mx-auto text-gray-200 mb-3" />
                                    <p>No invoices found.</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {invoices.map((invoice) => {
                                        const status = getStatusConfig(invoice.status)
                                        const StatusIcon = status.icon
                                        const balance = invoice.total - (invoice.partialAmount || 0)

                                        return (
                                            <div key={invoice.id} className="p-4 hover:bg-gray-50 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${status.bg} ${status.text}`}>
                                                        <StatusIcon size={18} />
                                                    </div>
                                                    <div>
                                                        <div className="flex flex-col">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-semibold text-gray-900">{invoice.invoiceNumber}</span>
                                                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${status.bg} ${status.text} sm:hidden`}>
                                                                    {status.label}
                                                                </span>
                                                            </div>
                                                            {invoice.subject && (
                                                                <span className="text-sm text-gray-600 font-medium truncate max-w-[200px] sm:max-w-xs">
                                                                    {invoice.subject}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-sm text-gray-500 flex gap-3 mt-0.5">
                                                            <span>{formatDate(invoice.issueDate)}</span>
                                                            <span>•</span>
                                                            <span className={invoice.status === 'overdue' ? 'text-red-600 font-medium' : ''}>
                                                                Due {formatDate(invoice.dueDate)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between sm:justify-end gap-6 w-full sm:w-auto pl-14 sm:pl-0">
                                                    <div className="text-right">
                                                        <p className="font-bold text-gray-900">{formatCurrency(invoice.total)}</p>
                                                        {balance > 0 && invoice.status !== 'draft' ? (
                                                            <p className="text-xs text-orange-600 font-medium">Due: {formatCurrency(balance)}</p>
                                                        ) : (
                                                            <span className={`hidden sm:inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${status.bg} ${status.text}`}>
                                                                {status.label}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <Link
                                                        to={`/portal/${invoice.id}`}
                                                        className="btn btn-secondary text-sm group"
                                                    >
                                                        View
                                                        <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                                                    </Link>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Global Activity Tab */}
                    {activeTab === 'global' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="card">
                                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                                    <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <ShoppingBag size={18} className="text-indigo-600" />
                                        External Purchase History
                                    </h2>
                                    <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded uppercase tracking-wider">Sync Active</span>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {globalOrders.length === 0 ? (
                                        <div className="p-12 text-center text-gray-500">
                                            <p>No external orders found for this email.</p>
                                        </div>
                                    ) : (
                                        globalOrders.map((order, idx) => (
                                            <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                                                        <ShoppingBag size={20} />
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-900">{order.orderId || `Order #${idx + 1}`}</p>
                                                        <p className="text-xs text-gray-500">{order.description || 'nCinq Digital Purchase'}</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-gray-900">{formatCurrency(order.amount / 100)}</p>
                                                    <p className="text-[10px] text-green-600 font-bold uppercase">{order.status || 'Completed'}</p>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tickets Tab */}
                    {activeTab === 'tickets' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                            {globalTickets.map((ticket, idx) => (
                                <div key={idx} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                                    <div className="bg-accent-600 p-4 text-white">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="text-xs font-bold uppercase tracking-widest opacity-80">Event Ticket</p>
                                                <h3 className="text-lg font-bold mt-1">{ticket.eventName || 'nCinq Event'}</h3>
                                            </div>
                                            <Ticket size={24} />
                                        </div>
                                    </div>
                                    <div className="p-6 flex-1 flex flex-col items-center justify-center text-center">
                                        <div className="bg-gray-50 p-4 rounded-2xl mb-4 border border-gray-100">
                                            {ticket.qrData ? (
                                                <div className="relative group">
                                                    <img
                                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(ticket.qrData)}`}
                                                        alt="Ticket QR Code"
                                                        className="w-32 h-32"
                                                    />
                                                    <div className="absolute inset-0 bg-white/10 group-hover:block hidden"></div>
                                                </div>
                                            ) : (
                                                <QrCode size={64} className="text-gray-300" />
                                            )}
                                        </div>
                                        <p className="font-bold text-gray-900">{ticket.ticketId || '#TKT-992'}</p>
                                        <p className="text-sm text-gray-500 mt-1">{ticket.type || 'General Admission'}</p>

                                        <div className="mt-6 pt-6 border-t border-gray-50 w-full flex justify-between text-xs">
                                            <div className="text-left">
                                                <p className="text-gray-400 uppercase font-bold tracking-tighter">Date</p>
                                                <p className="text-gray-900 font-bold">{ticket.date || 'TBA'}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-gray-400 uppercase font-bold tracking-tighter">Time</p>
                                                <p className="text-gray-900 font-bold">{ticket.time || 'TBA'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-3 bg-gray-50 border-t border-gray-100 flex justify-center">
                                        <button className="text-xs font-bold text-primary-600 flex items-center gap-1 hover:underline">
                                            <ExternalLink size={12} />
                                            View Full Ticket
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Contact Footer */}
                <div className="bg-white rounded-2xl p-6 text-center text-sm text-gray-500">
                    <p className="mb-2 font-medium text-gray-900">Need help?</p>
                    <div className="flex flex-wrap justify-center gap-6">
                        <a href={`mailto:${settings?.businessEmail}`} className="hover:text-primary-600 flex items-center gap-2">
                            <Mail size={16} /> {settings?.businessEmail}
                        </a>
                        {settings?.businessPhone && (
                            <a
                                href={getWhatsAppLink(settings.businessPhone)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-primary-600 flex items-center gap-2"
                            >
                                <MessageCircle size={16} /> WhatsApp
                            </a>
                        )}
                    </div>
                </div>
            </main>
        </div>
    )
}
